import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  CandidateApplication,
  CandidateWithApplications,
  UpdateCandidateStageRequest,
  CandidateApplicationListResponse,
  MessageResponse,
  RequestConfig,
  PaginationParams,
} from './types';

/**
 * Candidates Module - Handles all candidate management API operations
 * 
 * This module provides candidate management operations including stage tracking,
 * email communication, and bidirectional access (job-centric and candidate-centric views).
 */
export class CandidatesModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Job-Centric Candidate Operations
  // ============================================================================

  /**
   * Get all candidate applications for a specific job
   * GET /api/admin/jobs/:jobId/candidates
   */
  async getJobCandidates(
    jobId: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    const queryString = params ? this.buildQueryString(params) : '';
    const endpoint = queryString 
      ? `/api/admin/jobs/${jobId}/candidates?${queryString}`
      : `/api/admin/jobs/${jobId}/candidates`;
    
    return this.get<CandidateApplicationListResponse>(endpoint, config);
  }

  /**
   * Get candidate application details for a specific job
   * GET /api/admin/jobs/:jobId/candidates/:candidateId
   */
  async getCandidateApplication(
    jobId: string,
    candidateId: string,
    config?: RequestConfig
  ): Promise<CandidateApplication> {
    return this.get<CandidateApplication>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}`,
      config
    );
  }

  /**
   * Update candidate stage for a specific job application
   * PATCH /api/admin/jobs/:jobId/candidates/:candidateId/stage
   */
  async updateCandidateStage(
    jobId: string,
    candidateId: string,
    data: UpdateCandidateStageRequest,
    config?: RequestConfig
  ): Promise<CandidateApplication> {
    return this.patch<CandidateApplication>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/stage`,
      data,
      config
    );
  }

  /**
   * Approve candidate for next stage
   * POST /api/admin/jobs/:jobId/candidates/:candidateId/approve
   */
  async approveForNextStage(
    jobId: string,
    candidateId: string,
    notes?: string,
    config?: RequestConfig
  ): Promise<CandidateApplication> {
    return this.post<CandidateApplication>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/approve`,
      { notes },
      config
    );
  }

  /**
   * Reject candidate
   * POST /api/admin/jobs/:jobId/candidates/:candidateId/reject
   */
  async rejectCandidate(
    jobId: string,
    candidateId: string,
    reason: string,
    config?: RequestConfig
  ): Promise<CandidateApplication> {
    return this.post<CandidateApplication>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/reject`,
      { reason },
      config
    );
  }

  /**
   * Send email to candidate
   * POST /api/admin/jobs/:jobId/candidates/:candidateId/email
   */
  async sendCandidateEmail(
    jobId: string,
    candidateId: string,
    emailData: {
      subject: string;
      content: string;
      cc?: string[];
    },
    config?: RequestConfig
  ): Promise<MessageResponse> {
    return this.post<MessageResponse>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/email`,
      emailData,
      config
    );
  }

  // ============================================================================
  // Candidate-Centric Operations
  // ============================================================================

  /**
   * Map backend status to frontend stage name
   */
  private statusToStage(status: string): string {
    const statusMap: Record<string, string> = {
      'submitted': 'Applied',
      'reviewed': 'Resume Review',
      'shortlisted': 'Shortlisted',
      'interview_scheduled': 'Interview Scheduled',
      'interviewed': 'Interview Completed',
      'offered': 'Offer Extended',
      'hired': 'Hired',
      'rejected': 'Rejected',
      'withdrawn': 'Applied',
    };
    return statusMap[status] || status || 'Applied';
  }

  /**
   * Get all applications for a specific candidate (across all jobs)
   * GET /api/admin/candidates/:candidateId
   */
  async getCandidateApplications(
    candidateId: string,
    config?: RequestConfig
  ): Promise<CandidateWithApplications> {
    // The backend returns CandidateProfile directly, we need to wrap it
    const candidateProfile = await this.get<any>(
      `/api/admin/candidates/${candidateId}`,
      config
    );
    
    // Transform applications to CandidateApplication format
    const applications = (candidateProfile.applications || []).map((app: any) => ({
      id: app.id,
      job_id: app.job_id,
      candidate_id: app.user_id || candidateId, // Backend uses user_id
      resume_id: app.resume_id,
      current_stage: app.current_stage || this.statusToStage(app.status), // Use current_stage if available, otherwise map from status
      stage_history: app.stage_history || [], // Include if available
      applied_at: app.applied_at,
      updated_at: app.updated_at,
    }));
    
    // Transform to CandidateWithApplications format
    return {
      candidate: candidateProfile,
      applications,
    };
  }

  /**
   * Get candidates by stage (across all jobs)
   * GET /api/admin/candidates/by-stage?stage=:stage
   */
  async getCandidatesByStage(
    stage: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    const queryString = params ? this.buildQueryString({ ...params, stage }) : `stage=${stage}`;
    return this.get<CandidateApplicationListResponse>(
      `/api/admin/candidates/by-stage?${queryString}`,
      config
    );
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get candidates at a specific stage for a job
   */
  async getJobCandidatesByStage(
    jobId: string,
    stage: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    const candidates = await this.getJobCandidates(jobId, params, config);
    return candidates.filter(c => c.current_stage === stage);
  }

  /**
   * Move candidate to next stage in the workflow
   */
  async advanceToNextStage(
    jobId: string,
    candidateId: string,
    notes?: string,
    config?: RequestConfig
  ): Promise<CandidateApplication> {
    return this.approveForNextStage(jobId, candidateId, notes, config);
  }

  /**
   * Move candidate to a specific stage
   */
  async moveToStage(
    jobId: string,
    candidateId: string,
    stage: string,
    notes?: string,
    config?: RequestConfig
  ): Promise<CandidateApplication> {
    return this.updateCandidateStage(
      jobId,
      candidateId,
      { stage: stage as any, notes },
      config
    );
  }

  /**
   * Get candidate count by stage for a job
   */
  async getCandidateCountByStage(
    jobId: string,
    config?: RequestConfig
  ): Promise<Record<string, number>> {
    const candidates = await this.getJobCandidates(jobId, undefined, config);
    
    const counts: Record<string, number> = {};
    candidates.forEach(candidate => {
      const stage = candidate.current_stage;
      counts[stage] = (counts[stage] || 0) + 1;
    });
    
    return counts;
  }

  /**
   * Get candidates pending review (at Resume Review stage)
   */
  async getPendingReviewCandidates(
    jobId?: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    if (jobId) {
      return this.getJobCandidatesByStage(jobId, 'Resume Review', params, config);
    }
    return this.getCandidatesByStage('Resume Review', params, config);
  }

  /**
   * Get shortlisted candidates
   */
  async getShortlistedCandidates(
    jobId?: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    if (jobId) {
      return this.getJobCandidatesByStage(jobId, 'Shortlisted', params, config);
    }
    return this.getCandidatesByStage('Shortlisted', params, config);
  }

  /**
   * Get candidates with scheduled interviews
   */
  async getInterviewScheduledCandidates(
    jobId?: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    if (jobId) {
      return this.getJobCandidatesByStage(jobId, 'Interview Scheduled', params, config);
    }
    return this.getCandidatesByStage('Interview Scheduled', params, config);
  }

  /**
   * Get candidates with completed interviews
   */
  async getInterviewCompletedCandidates(
    jobId?: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    if (jobId) {
      return this.getJobCandidatesByStage(jobId, 'Interview Completed', params, config);
    }
    return this.getCandidatesByStage('Interview Completed', params, config);
  }

  /**
   * Get candidates with offers extended
   */
  async getOfferExtendedCandidates(
    jobId?: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    if (jobId) {
      return this.getJobCandidatesByStage(jobId, 'Offer Extended', params, config);
    }
    return this.getCandidatesByStage('Offer Extended', params, config);
  }

  /**
   * Get hired candidates
   */
  async getHiredCandidates(
    jobId?: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    if (jobId) {
      return this.getJobCandidatesByStage(jobId, 'Hired', params, config);
    }
    return this.getCandidatesByStage('Hired', params, config);
  }

  /**
   * Get rejected candidates
   */
  async getRejectedCandidates(
    jobId?: string,
    params?: PaginationParams,
    config?: RequestConfig
  ): Promise<CandidateApplicationListResponse> {
    if (jobId) {
      return this.getJobCandidatesByStage(jobId, 'Rejected', params, config);
    }
    return this.getCandidatesByStage('Rejected', params, config);
  }

  /**
   * Bulk approve candidates for next stage
   */
  async bulkApproveForNextStage(
    jobId: string,
    candidateIds: string[],
    notes?: string,
    config?: RequestConfig
  ): Promise<CandidateApplication[]> {
    const promises = candidateIds.map(candidateId =>
      this.approveForNextStage(jobId, candidateId, notes, config)
    );
    
    return Promise.all(promises);
  }

  /**
   * Bulk reject candidates
   */
  async bulkRejectCandidates(
    jobId: string,
    candidateIds: string[],
    reason: string,
    config?: RequestConfig
  ): Promise<CandidateApplication[]> {
    const promises = candidateIds.map(candidateId =>
      this.rejectCandidate(jobId, candidateId, reason, config)
    );
    
    return Promise.all(promises);
  }

  /**
   * Bulk move candidates to a specific stage
   */
  async bulkMoveToStage(
    jobId: string,
    candidateIds: string[],
    stage: string,
    notes?: string,
    config?: RequestConfig
  ): Promise<CandidateApplication[]> {
    const promises = candidateIds.map(candidateId =>
      this.moveToStage(jobId, candidateId, stage, notes, config)
    );
    
    return Promise.all(promises);
  }

  /**
   * Send bulk emails to candidates
   */
  async bulkSendEmails(
    jobId: string,
    candidateIds: string[],
    emailData: {
      subject: string;
      content: string;
      cc?: string[];
    },
    config?: RequestConfig
  ): Promise<MessageResponse[]> {
    const promises = candidateIds.map(candidateId =>
      this.sendCandidateEmail(jobId, candidateId, emailData, config)
    );
    
    return Promise.all(promises);
  }

  /**
   * Get candidate application history
   */
  async getCandidateHistory(
    jobId: string,
    candidateId: string,
    config?: RequestConfig
  ): Promise<CandidateApplication> {
    const application = await this.getCandidateApplication(jobId, candidateId, config);
    return application;
  }

  /**
   * Check if candidate can be moved to next stage
   */
  canAdvanceToNextStage(currentStage: string): boolean {
    const stageOrder = [
      'Applied',
      'Resume Review',
      'Shortlisted',
      'Interview Scheduled',
      'Interview Completed',
      'Offer Extended',
      'Hired'
    ];
    
    return stageOrder.includes(currentStage) && currentStage !== 'Hired' && currentStage !== 'Rejected';
  }

  /**
   * Get next stage in the workflow
   */
  getNextStage(currentStage: string): string | null {
    const stageOrder = [
      'Applied',
      'Resume Review',
      'Shortlisted',
      'Interview Scheduled',
      'Interview Completed',
      'Offer Extended',
      'Hired'
    ];
    
    const currentIndex = stageOrder.indexOf(currentStage);
    if (currentIndex === -1 || currentIndex === stageOrder.length - 1) {
      return null;
    }
    
    return stageOrder[currentIndex + 1];
  }

  /**
   * Validate stage transition
   */
  validateStageTransition(fromStage: string, toStage: string): { valid: boolean; error?: string } {
    const stageOrder = [
      'Applied',
      'Resume Review',
      'Shortlisted',
      'Interview Scheduled',
      'Interview Completed',
      'Offer Extended',
      'Hired'
    ];
    
    // Allow moving to Rejected from any stage
    if (toStage === 'Rejected') {
      return { valid: true };
    }
    
    // Check if stages are valid
    if (!stageOrder.includes(fromStage)) {
      return { valid: false, error: `Invalid current stage: ${fromStage}` };
    }
    
    if (!stageOrder.includes(toStage)) {
      return { valid: false, error: `Invalid target stage: ${toStage}` };
    }
    
    // Allow moving forward or backward in the workflow
    return { valid: true };
  }

  // ============================================================================
  // Video Management
  // ============================================================================

  /**
   * Upload a video
   * POST /api/user/videos/upload
   */
  async uploadVideo(request: any): Promise<any> {
    return this.post<any>('/api/user/videos/upload', request);
  }

  /**
   * Get user's videos
   * GET /api/user/videos
   */
  async getUserVideos(): Promise<any> {
    return this.get<any>('/api/user/videos');
  }

  /**
   * Delete a video
   * DELETE /api/user/videos/:id
   */
  async deleteVideo(id: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/api/user/videos/${id}`);
  }

  // ============================================================================
  // YouTube Video Integration
  // ============================================================================

  /**
   * Get user's YouTube videos
   * GET /api/user/youtube/videos
   */
  async getYouTubeVideos(maxResults?: number): Promise<any[]> {
    const params = maxResults ? { max_results: maxResults } : {};
    return this.get<any[]>('/api/user/youtube/videos', { params });
  }

  /**
   * Link a YouTube video to user profile
   * POST /api/user/videos/youtube
   */
  async linkYouTubeVideo(request: any): Promise<any> {
    return this.post<any>('/api/user/videos/youtube', request);
  }
}
